// Based on https://github.com/glauberramos/vanilla-emoji-picker
import { EmojiList } from './constants';
import {
  forEach, map, isDescendant, addClass
} from './utils';

class EmojiPicker {
  constructor(triggetButton, editor, restoreSelection) {
    // Passing restoreSelection method of editor to emoji picker
    this.restoreSelection = restoreSelection;

    // Bindings
    this.generateEmojiList = this.generateEmojiList.bind(this);
    this.generateEmojiButton = this.generateEmojiButton.bind(this);
    this.generateEmojiPicker = this.generateEmojiPicker.bind(this);
    this.generateEmojiListItem = this.generateEmojiListItem.bind(this);
    this.handleEmojiButtonClick = this.handleEmojiButtonClick.bind(this);
    this.handleEmojiTriggerClick = this.handleEmojiTriggerClick.bind(this);

    // Binding emoji button and editor for ease to access
    this.editor = editor;
    this.triggetButton = triggetButton;

    // Generate emoji picker
    this.emojiPicker = this.generateEmojiPicker(editor);

    // Create container for styling
    this.emojiContainer = document.createElement('div');
    this.emojiContainer.style.position = 'absolute';

    // Place picker into container
    this.emojiContainer.appendChild(this.emojiPicker);

    // Append picker to the parent of trigger button
    this.triggetButton.parentNode.appendChild(this.emojiContainer);

    // Handle trigger event
    this.triggetButton.addEventListener('click', this.handleEmojiTriggerClick);

    // Handle emoji picker visibility
    this.triggetButton.addEventListener('blur', () => setTimeout(() => { this.emojiPicker.style.display = 'none'; }, 200));
  }

  generateEmojiPicker(editor) {
    // Create empty containers
    const emojiPicker = document.createElement('div');

    // Create emoji list
    const emojiList = this.generateEmojiList();

    // Required styling for emoji picker
    addClass(emojiPicker, 'jfEmoji-picker');
    // Hide picker on blur
    emojiPicker.addEventListener('blur', () => {
      emojiPicker.style.display = 'none';
    });

    // Hide picker on editor typing
    editor.addEventListener('keydown', () => {
      emojiPicker.style.display = 'none';
    });

    // Place list into picker
    emojiPicker.appendChild(emojiList);

    return emojiPicker;
  }

  generateEmojiListItem() {
    // Create empty list item
    const listItem = document.createElement('li');
    listItem.className = 'jfEmoji-picker-item';

    return listItem;
  }

  generateEmojiButton(emoji) {
    // Create empty anchor
    const emojiLink = document.createElement('a');
    emojiLink.className = 'jfEmoji-picker-link';
    emojiLink.innerHTML = String.fromCodePoint(emoji);

    return emojiLink;
  }

  generateEmojiList() {
    // Container for emoji list
    const emojiList = document.createElement('ul');
    emojiList.className = 'jfEmoji-picker-list';

    // Generate list item for each emoji and insert them into container
    const emojiListItems = map(EmojiList, emoji => {
      // Generate dom elements
      const listItem = this.generateEmojiListItem();
      const emojiButton = this.generateEmojiButton(emoji);

      // Bind emoji click handling
      emojiButton.addEventListener('click', this.handleEmojiButtonClick);

      // Place button into list item
      listItem.appendChild(emojiButton);

      return listItem;
    });

    // Place each list item into emoji container
    forEach(emojiListItems, listItem => emojiList.appendChild(listItem));

    return emojiList;
  }

  handleEmojiButtonClick(event) {
    // Restore editor selection
    this.restoreSelection();

    // Get selection properties
    const selection = global.getSelection();
    const {
      focusNode,
      anchorOffset,
      focusOffset
    } = selection;

    // Do not try to paste emoji into text if current selection is not the editor
    if (focusNode !== this.editor && !isDescendant(this.editor, focusNode)) {
      // Hide emoji picker and exit from function
      this.emojiPicker.style.display = 'none';
      return false;
    }

    /*
    * Relation between anchorOffset and focusOffset indicates the selection direction.
    * If anchorOffset is the smaller one, then selection started from left; otherwise right.
    */

    const isLeft = anchorOffset < focusOffset;

    // Decide start and end positions
    const startPosition = isLeft ? anchorOffset : focusOffset;
    const endPosition = isLeft ? focusOffset : anchorOffset;

    // Seperate string into parts to place emoji inbetween
    const startPart = focusNode.textContent.substring(0, startPosition);
    const endPart = focusNode.textContent.substring(endPosition);
    const emojiPart = event.target.textContent;

    // Updating base node text
    focusNode.textContent = startPart + emojiPart + endPart;

    // Changing text content resets the selection so we need to set it again
    const range = document.createRange();

    // Update caret position
    if (startPart.length > 0) {
      range.setStart(focusNode, startPart.length + emojiPart.length);
    } else { // when adding emoji as the first character, length is not calculated correctly.
      range.setStart(focusNode, startPart.length);
    }
    // Marking it as collapses which means there is not any selection
    range.collapse(true);
    // Clear ranges and add the range we have created
    selection.removeAllRanges();
    selection.addRange(range);

    // Hide emoji picker after selection
    this.emojiPicker.style.display = 'none';
  }

  handleEmojiTriggerClick({ target }) {
    // Only trigger when focused element is trigger button
    if (target !== this.triggetButton) return;

    if (this.emojiPicker.style.display === 'block') {
      // Hide emoji picker
      this.emojiPicker.style.display = 'none';
    } else {
      // Show emoji picker
      this.emojiPicker.style.display = 'block';
      const btnRect = this.triggetButton.getBoundingClientRect();
      const parentRect = this.triggetButton.parentNode.getBoundingClientRect();
      const emojiPickerWidth = this.emojiPicker.getBoundingClientRect().width;
      const spaceLeft = btnRect.left - (emojiPickerWidth / 2);
      // Shift emojiPicker to the right if there is not enough space on the left.
      const neededSpace = spaceLeft < 0 ? Math.abs(spaceLeft) : 0;
      this.emojiContainer.style.left = `${(btnRect.left - parentRect.left) + 40 + neededSpace}px`;
    }
  }
}

export default EmojiPicker;
